import React, { useCallback } from 'react'
import { useSnack } from '@reapit/elements'
import { useLoadScript } from '../hooks/useLoadScript'
import { User, Office, SalesforceChatConfig } from '../types'

declare global {
  interface Window {
    embeddedservice_bootstrap?: any
  }
}

/**
 * Defines the structure for Salesforce Embedded Messaging configuration.
 */
interface LoadSalesforceLiveChatProps {
  /**
   * The Salesforce chat configuration to use for initialization.
   * This object will be passed entirely from the consuming application.
   */
  config: SalesforceChatConfig
  /**
   * The current user data from the consuming application.
   */
  user: User | null
  /**
   * The current office data from the consuming application.
   */
  office: Office | null
}

/**
 * `LoadSalesforceLiveChat` is a React component responsible for:
 * 1. Dynamically loading the Salesforce Embedded Messaging JavaScript SDK using the provided config.
 * 2. Initializing the Salesforce chat widget with the necessary configuration details.
 * 3. Populating pre-chat fields (visible and hidden) with current user and office data.
 *
 * This component accepts config, user, and office data as props from the consuming application.
 * It should be rendered conditionally only when LiveChat is available.
 */
export const LoadSalesforceLiveChat: React.FC<LoadSalesforceLiveChatProps> = ({ config, user, office }) => {
  const { error } = useSnack()
  // Callback to initialize Embedded Messaging and set pre-chat fields
  const initialiseLiveChatAndIdentifyUser = useCallback(() => {
    if (!window.embeddedservice_bootstrap) {
      error('Unable to load Support Chat. The chat service is not available.')
      return () => {}
    }

    try {
      window.embeddedservice_bootstrap.settings.language = 'en'
      window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true

      window.embeddedservice_bootstrap.init(config.orgId, config.deploymentId, config.communityUrl, {
        scrt2URL: config.scrt2URL,
      })

      // Define the handler function
      const onEmbeddedMessagingReadyHandler = () => {
        if (!user || !office) {
          return
        }

        window.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
          _firstName: { value: user.firstName, isEditableByEndUser: true },
          _lastName: { value: user.lastName, isEditableByEndUser: true },
          _email: { value: user.email, isEditableByEndUser: true },
        })

        window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          Office_Login: user.systemLogin,
          Product: 'Agentbox',
          User_Role: user.role,
          Main_Office: office.name,
          System_Type: office.systemType,
          Locale: office.locale,
          Additional_Telemetry: office.additionalTelemetry || '',
          Telemetry_2: office.telemetry2 || '',
        })
      }

      // Add the event listener
      window.addEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReadyHandler)

      // Cleanup function for this specific event listener
      return () => {
        window.removeEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReadyHandler)
      }
    } catch (err) {
      error(String(err))
    }
  }, [config, user, office])

  useLoadScript({
    src: config.bootstrapScriptUrl,
    onLoad: () => {
      initialiseLiveChatAndIdentifyUser()
    },
    id: 'salesforce-embedded-messaging-script',
  })

  return null
}
